import React, { useRef, useState, useCallback, useEffect } from 'react';
import mapData from './map_data.json';
import { getRegionColor } from './theme';

const VB = { x: 0, y: 0, w: 1000, h: 400 };
const MIN_W = VB.w / 10;

const fmtM = (v) => v >= 1e6 ? '$' + (v / 1e6).toFixed(v / 1e6 >= 10 ? 0 : 1) + 'M' : '$' + (v / 1e3).toFixed(0) + 'k';

const LEGEND = [
  { label: 'Americas', color: '#FF6B52' },
  { label: 'Europe', color: '#4FC3C9' },
  { label: 'Asia', color: '#F2B14C' },
  { label: 'Mid East', color: '#B98CD6' },
  { label: 'Africa', color: '#8FD694' },
  { label: 'Oceania', color: '#F08FA8' },
];

export const WhiteSpaceMap = ({ countries, selected, onSelect }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [vb, setVb] = useState({ ...VB });
  const [hover, setHover] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragRef = useRef({ down: false, sx: 0, sy: 0, vx: 0, vy: 0 });

  const zoom = vb.w > 0 ? VB.w / vb.w : 1;

  const clamp = useCallback((v) => {
    let { x, y, w, h } = v;
    if (w > VB.w) w = VB.w;
    if (h > VB.h) h = VB.h;
    if (w < MIN_W) { w = MIN_W; h = MIN_W * VB.h / VB.w; }
    if (x < VB.x) x = VB.x;
    if (y < VB.y) y = VB.y;
    if (x + w > VB.x + VB.w) x = VB.x + VB.w - w;
    if (y + h > VB.y + VB.h) y = VB.y + VB.h - h;
    return { x, y, w, h };
  }, []);

  const zoomAt = useCallback((lx, ly, factor) => {
    setVb(prev => {
      const nw = Math.min(VB.w, Math.max(MIN_W, prev.w * factor));
      const nh = nw * VB.h / VB.w;
      const fx = (lx - prev.x) / prev.w;
      const fy = (ly - prev.y) / prev.h;
      return clamp({ x: lx - fx * nw, y: ly - fy * nh, w: nw, h: nh });
    });
  }, [clamp]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const loc = pt.matrixTransform(svg.getScreenCTM().inverse());
    zoomAt(loc.x, loc.y, e.deltaY < 0 ? 0.82 : 1.22);
  }, [zoomAt]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    const onFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fsEl === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el);
    }
  }, []);

  const handlePointerDown = (e) => {
    dragRef.current = { down: true, sx: e.clientX, sy: e.clientY, vx: vb.x, vy: vb.y };
  };
  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d.down) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const nx = d.vx - (e.clientX - d.sx) * vb.w / rect.width;
    const ny = d.vy - (e.clientY - d.sy) * vb.h / rect.height;
    setVb(prev => clamp({ ...prev, x: nx, y: ny }));
  };
  const handlePointerUp = () => { dragRef.current.down = false; };

  const handleBubbleMove = (e) => {
    const box = containerRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    let x = e.clientX - rect.left + 12, y = e.clientY - rect.top + 12;
    if (x > rect.width - 150) x -= 160;
    setHoverPos({ x, y });
  };

  if (!countries || !countries.length) return null;

  const maxVal = Math.max(...countries.map(c => c.val), 1);
  const sorted = [...countries].sort((a, b) => b.val - a.val);
  const f = Math.pow(zoom, 0.5);
  const labelOpacity = zoom < 1.8 ? 0 : Math.min(0.85, (zoom - 1.8) * 0.5);
  const selectedCountry = selected ? countries.find(c => c.name === selected) : null;

  return (
    <div
      ref={containerRef}
      className={`relative bg-dash-panel border border-dash-line overflow-hidden ${isFullscreen ? 'flex items-center justify-center rounded-none' : 'rounded-xl'}`}
      data-testid="whitespace-map"
    >
      <svg
        ref={svgRef}
        viewBox={`${vb.x.toFixed(1)} ${vb.y.toFixed(1)} ${vb.w.toFixed(1)} ${vb.h.toFixed(1)}`}
        className="w-full cursor-grab active:cursor-grabbing"
        style={{ height: isFullscreen ? '100vh' : '400px' }}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <path d={mapData.world_path} fill="var(--dash-panel)" stroke="var(--dash-line)" strokeWidth={0.5 / f} />

        {sorted.map(c => {
          const coord = mapData.coords[c.name];
          if (!coord) return null;
          const r = Math.max(3, Math.sqrt(c.val / maxVal) * 36) / f;
          const isSel = selected === c.name;
          const color = getRegionColor(c.name);
          return (
            <circle
              key={c.name}
              cx={coord.x}
              cy={coord.y}
              r={r}
              fill={color}
              fillOpacity={isSel ? 0.95 : 0.6}
              stroke={isSel ? '#fff' : (c.in_market ? '#EAF4F2' : color)}
              strokeWidth={(isSel ? 2.4 : (c.in_market ? 1.6 : 1)) / f}
              className="cursor-pointer transition-opacity hover:fill-opacity-95"
              onClick={(e) => { e.stopPropagation(); onSelect(c.name); }}
              onPointerEnter={(e) => { setHover(c); handleBubbleMove(e); }}
              onPointerMove={handleBubbleMove}
              onPointerLeave={() => setHover(null)}
            />
          );
        })}

        <g style={{ opacity: labelOpacity, transition: 'opacity 0.3s' }}>
          {sorted.map(c => {
            const coord = mapData.coords[c.name];
            if (!coord) return null;
            const r = Math.max(3, Math.sqrt(c.val / maxVal) * 36) / f;
            return (
              <text
                key={'lbl-' + c.name}
                x={coord.x}
                y={coord.y - r - 4 / f}
                textAnchor="middle"
                className="font-grotesk font-semibold"
                style={{ fontSize: `${8.5 / zoom}px`, fill: 'var(--dash-mut)', stroke: 'var(--dash-bg)', strokeWidth: `${2.6 / zoom}px`, paintOrder: 'stroke' }}
              >
                {c.name}
              </text>
            );
          })}
        </g>
      </svg>

      {/* Corner info box for the selected country */}
      {selectedCountry && (
        <div
          className="absolute bottom-3 left-3 bg-[rgba(8,21,25,.9)] border border-dash-amber rounded-xl px-3.5 py-2.5 max-w-[60%] shadow-lg z-10"
          data-testid="whitespace-map-info"
        >
          <b className="font-grotesk font-bold text-base block text-dash-ink">{selectedCountry.name}</b>
          <span className="font-mono text-[10.5px] text-dash-ice uppercase">
            {selectedCountry.region}{selectedCountry.in_market ? ' · OUR MARKET' : ''}
          </span>
          <div className="font-mono text-[15px] mt-1.5 text-dash-ink">
            {fmtM(selectedCountry.val)} <em className="not-italic text-dash-amber font-bold">{selectedCountry.ports} ports</em>
          </div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
        {[
          { key: 'in', label: '+', title: 'Zoom in', action: () => zoomAt(vb.x + vb.w / 2, vb.y + vb.h / 2, 0.66) },
          { key: 'out', label: '−', title: 'Zoom out', action: () => zoomAt(vb.x + vb.w / 2, vb.y + vb.h / 2, 1.5) },
          { key: 'reset', label: '↻', title: 'Reset', action: () => setVb({ ...VB }) },
          {
            key: 'full',
            title: isFullscreen ? 'Exit full screen' : 'Full screen',
            action: toggleFullscreen,
            label: (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                {isFullscreen
                  ? <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
                  : <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />}
              </svg>
            ),
          },
        ].map(b => (
          <button
            key={b.key}
            onClick={b.action}
            title={b.title}
            className="w-7 h-7 rounded-md bg-dash-panel border border-dash-line text-dash-ink text-sm flex items-center justify-center cursor-pointer hover:bg-dash-panel2 transition"
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="absolute right-2.5 bottom-2.5 flex flex-wrap gap-x-3 gap-y-1.5 font-mono text-[10px] text-dash-mut bg-[rgba(8,21,25,.66)] border border-dash-line rounded-lg px-2.5 py-1.5 z-10">
        {LEGEND.map(l => (
          <span key={l.label} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Hover tooltip — follows the cursor */}
      {hover && (
        <div
          className="absolute bg-dash-panel/95 border border-dash-amber rounded-lg px-3 py-2 text-xs font-mono pointer-events-none z-10"
          style={{ left: hoverPos.x, top: hoverPos.y }}
        >
          <div className="text-dash-ink font-bold">{hover.name}</div>
          <div className="text-dash-mut">{fmtM(hover.val)} · {hover.ports} ports</div>
        </div>
      )}
    </div>
  );
};
